import Handlebars from 'handlebars';
import type { ResumeContent, ResumeSettings } from '../types';
import { getColorScheme, getDefaultColorScheme, generateColorSchemeCSS, generateTemplateOverrideCSS } from './color-schemes';
import { normalizeResumeContent, normalizeTextValue } from '../normalize-content';
import { looksLikeHtml, renderContentBlocksHtml } from '../list-content';
import { OPEN_SANS_FONT_FACE_CSS } from './open-sans-font-face';
import { RESUME_PAGINATION_CSS } from './pagination';
import { templateSupportsProfilePhoto } from './registry';
import { isRenderableProfilePictureUrl } from '../profile-picture';

const RESUME_FONT_FAMILY = "'Open Sans', Arial, sans-serif";
const SHARED_FONT_IMPORTS = OPEN_SANS_FONT_FACE_CSS;

// Register Handlebars helpers
Handlebars.registerHelper('formatDate', function (dateString: string) {
  if (!dateString) return '';
  const [year, month] = dateString.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(month) - 1]} ${year}`;
});

Handlebars.registerHelper('ifEquals', function (this: unknown, arg1: unknown, arg2: unknown, options: Handlebars.HelperOptions) {
  return arg1 === arg2 ? options.fn(this) : options.inverse(this);
});

Handlebars.registerHelper('linkIcon', function (type: string) {
  const icons: Record<string, string> = {
    linkedin: '&#xf08c;', // FontAwesome LinkedIn icon code
    github: '&#xf09b;',
    twitter: '&#xf099;',
    portfolio: '&#xf0ac;',
    custom: '&#xf0c1;',
  };
  return new Handlebars.SafeString(icons[type] || icons.custom);
});

Handlebars.registerHelper('initials', function (firstName?: string, lastName?: string) {
  const first = typeof firstName === 'string' ? firstName.trim().charAt(0) : '';
  const last = typeof lastName === 'string' ? lastName.trim().charAt(0) : '';
  const value = `${first}${last}`.toUpperCase();
  return value || 'CV';
});

Handlebars.registerHelper('stripHtml', function (html: string) {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').trim();
});

/**
 * `{{{renderContent value}}}` renders a plain-text field.
 *
 * `autoList=true` opts the field into list recovery: content that reads as a
 * list of items (see `lib/resume/list-content`) becomes a real `<ul>`/`<ol>`
 * instead of `<br/>`-separated lines. Pass it for fields that are conceptually
 * item lists — employment responsibilities, custom sections — and leave it off
 * for prose such as the profile summary, where an explicit `- ` marker is still
 * honoured but consecutive lines are not guessed at.
 *
 * Anything that is not a list falls through to the legacy escape-and-`<br/>`
 * path unchanged.
 */
Handlebars.registerHelper('renderContent', function (value: unknown, options?: Handlebars.HelperOptions) {
  const normalizedValue = normalizeTextValue(value);
  if (!normalizedValue) return '';

  // Preserve existing rich-text HTML content when present.
  if (looksLikeHtml(normalizedValue)) {
    return new Handlebars.SafeString(normalizedValue);
  }

  const autoList = Boolean(options?.hash?.autoList);
  const listHtml = renderContentBlocksHtml(normalizedValue, { autoList });
  if (listHtml !== null) {
    return new Handlebars.SafeString(listHtml);
  }

  // For plain textarea content, preserve authored line breaks in HTML output.
  const normalized = normalizedValue.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const escaped = Handlebars.escapeExpression(normalized).replace(/\n/g, '<br/>');
  return new Handlebars.SafeString(escaped);
});

/**
 * `{{{consentText value}}}` renders the data-processing consent clause.
 *
 * Deliberately narrower than `renderContent`: the clause is a legal sentence
 * the user typed into a textarea, so it is always escaped — there is no
 * rich-text passthrough to abuse — and it is never re-read as a list. Authored
 * line breaks survive as `<br/>`.
 */
Handlebars.registerHelper('consentText', function (value: unknown) {
  const text = normalizeTextValue(value).replace(/\r\n?/g, '\n').trim();
  if (!text) return '';

  const escaped = Handlebars.escapeExpression(text).replace(/\n/g, '<br/>');
  return new Handlebars.SafeString(escaped);
});

// Base template styles
const baseStyles = `
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: ${RESUME_FONT_FAMILY};
    font-size: 11pt;
    line-height: 1.4;
    color: var(--resume-color-text);
    background: var(--resume-color-background);
  }

  .resume {
    width: 210mm;
    min-height: 297mm;
    padding: 15mm;
    margin: 0 auto;
    background: white;
  }

  h1 {
    font-size: 24pt;
    color: var(--resume-color-primary);
    margin-bottom: 4px;
  }

  h2 {
    font-size: 12pt;
    color: var(--resume-color-primary);
    border-bottom: 2px solid var(--resume-color-primary);
    padding-bottom: 4px;
    margin: 16px 0 10px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  h3 {
    font-size: 11pt;
    color: var(--resume-color-secondary);
    margin-bottom: 2px;
  }

  .headline {
    font-size: 13pt;
    color: var(--resume-color-muted);
    margin-bottom: 8px;
  }

  /*
   * Only applied when a photo is actually rendered — without one the header
   * keeps its original plain-block flow, so photo-free resumes are unchanged
   * byte for byte.
   */
  .header-with-photo {
    display: flex;
    align-items: flex-start;
    gap: 6mm;
  }

  .header-with-photo .header-identity {
    flex: 1 1 auto;
    min-width: 0;
  }

  .profile-photo {
    flex: 0 0 auto;
    width: 28mm;
    height: 28mm;
    border-radius: 999px;
    object-fit: cover;
    background: var(--resume-color-background);
  }

  .contact-info {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    font-size: 10pt;
    color: var(--resume-color-muted);
    margin-bottom: 8px;
  }

  .contact-item {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .links {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 12px;
  }

  .link {
    font-size: 10pt;
    color: var(--resume-color-primary);
    text-decoration: none;
  }

  .summary {
    margin-bottom: 12px;
    text-align: justify;
  }

  .section {
    margin-bottom: 16px;
  }

  .entry {
    margin-bottom: 12px;
  }

  .entry-header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 4px;
  }

  .entry-title {
    font-weight: 600;
  }

  .entry-subtitle {
    color: var(--resume-color-muted);
    font-size: 10pt;
  }

  .entry-date {
    color: var(--resume-color-muted);
    font-size: 10pt;
    white-space: nowrap;
  }

  .entry-content {
    font-size: 10pt;
  }

  .entry-content ul {
    margin-left: 16px;
  }

  .entry-content li {
    margin-bottom: 2px;
  }

  /* Lists recovered from plain-text fields; see lib/resume/list-content.ts.
     Carries its own indent because the reset above zeroes list padding, which
     would otherwise leave the marker outside the paper. */
  .content-list {
    margin: 2px 0 0 16px;
    padding: 0;
  }

  .content-list li {
    margin-bottom: 2px;
  }

  /* Data-processing consent clause. Fine print that closes the document, so it
     is set below every section and must not read as one: no heading, no rule,
     smaller and muted so it never competes with the content above it. */
  .consent-note {
    margin-top: 16px;
    font-size: 8pt;
    line-height: 1.35;
    font-style: italic;
    color: var(--resume-color-muted);
  }

  .skills-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .skill-tag {
    background: var(--resume-color-primary);
    color: white;
    padding: 2px 8px;
    border-radius: 3px;
    font-size: 9pt;
  }

  .skill-category {
    margin-bottom: 8px;
  }

  .skill-category-name {
    font-weight: 600;
    font-size: 10pt;
    margin-bottom: 4px;
  }

  @media print {
    body {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .resume {
      width: 100% !important;
      min-height: auto !important;
      padding: 0 !important;
      margin: 0 !important;
    }

  }
`;

// Classic template
const classicTemplate = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>{{personalInfo.firstName}} {{personalInfo.lastName}} - Resume</title>
  <style>
    ${SHARED_FONT_IMPORTS}
    :root {
      {{colorSchemeCSS}}
      {{templateColorCSS}}
    }
    ${baseStyles}
  ${RESUME_PAGINATION_CSS}
  </style>
</head>
<body>
  <div class="resume">
    <header{{#if profilePicture}} class="header-with-photo"{{/if}}>
      {{#if profilePicture}}
      <img class="profile-photo" src="{{profilePicture.url}}" alt="">
      {{/if}}
      <div class="header-identity">
      <h1><span data-rf="personalInfo.firstName">{{personalInfo.firstName}}</span> <span data-rf="personalInfo.lastName">{{personalInfo.lastName}}</span></h1>
      {{#if profile.headline}}
      <div class="headline" data-rf="profile.headline">{{profile.headline}}</div>
      {{/if}}

      <div class="contact-info">
        {{#if contact.email}}
        <span class="contact-item"><span data-rf="contact.email">{{contact.email}}</span></span>
        {{/if}}
        {{#if contact.phone}}
        <span class="contact-item"><span data-rf="contact.phone">{{contact.phone}}</span></span>
        {{/if}}
        {{#if contact.location}}
        <span class="contact-item"><span data-rf="contact.location">{{contact.location}}</span></span>
        {{/if}}
      </div>

      {{#if links.length}}
      <div class="links">
        {{#each links}}
        <a href="{{url}}" class="link">{{#if label}}{{label}}{{else}}{{type}}{{/if}}</a>
        {{/each}}
      </div>
      {{/if}}
      </div>
    </header>

    {{#if profile.summary}}
    <section class="section">
      <h2>Professional Summary</h2>
      <div class="summary" data-rf="profile.summary" data-rf-multiline="true">{{{renderContent profile.summary}}}</div>
    </section>
    {{/if}}

    {{#if employment.length}}
    <section class="section">
      <h2>Experience</h2>
      {{#each employment}}
      <div class="entry">
        <div class="entry-header">
          <div>
            <span class="entry-title" data-rf="employment.{{id}}.title">{{title}}</span>
            <span class="entry-subtitle"> - {{company}}{{#if location}}, {{location}}{{/if}}</span>
          </div>
          <span class="entry-date" data-rf-date="employment.{{id}}">
            {{formatDate startDate}} - {{#if isCurrentlyWorking}}Present{{else}}{{formatDate endDate}}{{/if}}
          </span>
        </div>
        <div class="entry-content" data-rf="employment.{{id}}.responsibilities" data-rf-multiline="true">{{{renderContent responsibilities autoList=true}}}</div>
      </div>
      {{/each}}
    </section>
    {{/if}}

    {{#if education.length}}
    <section class="section">
      <h2>Education</h2>
      {{#each education}}
      <div class="entry">
        <div class="entry-header">
          <div>
            <span class="entry-title"><span data-rf="education.{{id}}.degree">{{degree}}</span>{{#if field}} in <span data-rf="education.{{id}}.field">{{field}}</span>{{/if}}</span>
            <span class="entry-subtitle"> - <span data-rf="education.{{id}}.institution">{{institution}}</span></span>
          </div>
          <span class="entry-date" data-rf-date="education.{{id}}">
            {{#if endDate}}{{formatDate endDate}}{{else if startDate}}{{formatDate startDate}}{{/if}}
          </span>
        </div>
        {{#if gpa}}
        <div class="entry-content">GPA: <span data-rf="education.{{id}}.gpa">{{gpa}}</span></div>
        {{/if}}
      </div>
      {{/each}}
    </section>
    {{/if}}

    {{#if (or skills.technical.length skills.soft.length skills.categories.length)}}
    <section class="section">
      <h2>Skills</h2>
      {{#if skills.technical.length}}
      <div class="skill-category">
        <div class="skill-category-name">Technical Skills</div>
        <div class="skills-grid">
          {{#each skills.technical}}
          <span class="skill-tag" data-rf="skills.technical.{{@index}}">{{this}}</span>
          {{/each}}
        </div>
      </div>
      {{/if}}
      {{#if skills.soft.length}}
      <div class="skill-category">
        <div class="skill-category-name">Soft Skills</div>
        <div class="skills-grid">
          {{#each skills.soft}}
          <span class="skill-tag" data-rf="skills.soft.{{@index}}">{{this}}</span>
          {{/each}}
        </div>
      </div>
      {{/if}}
      {{#each skills.categories}}
      <div class="skill-category">
        <div class="skill-category-name">{{name}}</div>
        <div class="skills-grid">
          {{#each skills}}
          <span class="skill-tag" data-rf="skillCategories.{{../id}}.{{@index}}">{{this}}</span>
          {{/each}}
        </div>
      </div>
      {{/each}}
    </section>
    {{/if}}

    {{#if certifications.length}}
    <section class="section">
      <h2>Certifications</h2>
      {{#each certifications}}
      <div class="entry">
        <div class="entry-header">
          <div>
            <span class="entry-title"><span data-rf="certifications.{{id}}.name">{{name}}</span></span>
            <span class="entry-subtitle"> - <span data-rf="certifications.{{id}}.issuer">{{issuer}}</span></span>
          </div>
          {{#if date}}
          <span class="entry-date" data-rf-date="certifications.{{id}}">{{formatDate date}}</span>
          {{/if}}
        </div>
      </div>
      {{/each}}
    </section>
    {{/if}}

    {{#each customSections}}
    <section class="section">
      <h2>{{title}}</h2>
      <div class="entry-content" data-rf="customSections.{{id}}.content" data-rf-multiline="true">{{{renderContent content autoList=true}}}</div>
    </section>
    {{/each}}

    {{#if consent.enabled}}{{#if consent.text}}
    <div class="consent-note">{{{consentText consent.text}}}</div>
    {{/if}}{{/if}}
  </div>
</body>
</html>
`;

// Modern template - more spaced out, sans-serif focused
const modernTemplate = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>{{personalInfo.firstName}} {{personalInfo.lastName}} - Resume</title>
  <style>
    ${SHARED_FONT_IMPORTS}
    :root {
      {{colorSchemeCSS}}
      {{templateColorCSS}}
      --modern-h1: var(--resume-color-secondary);
      --modern-heading: var(--resume-color-primary);
      --modern-rule: var(--resume-color-accent);
      --modern-link: var(--resume-color-secondary);
      --modern-skill-text: var(--resume-color-secondary);
      --modern-skill-border: var(--resume-color-accent);
    }
    ${baseStyles}

    body {
      font-family: ${RESUME_FONT_FAMILY};
    }

    .resume {
      padding: 20mm;
    }

    h1 {
      font-weight: 300;
      font-size: 32pt;
      letter-spacing: -1px;
      color: var(--modern-h1);
    }

    h2 {
      border-bottom: 1px solid var(--modern-rule);
      color: var(--modern-heading);
      font-weight: 600;
      font-size: 11pt;
      margin-top: 20px;
      padding-bottom: 3px;
    }

    .headline {
      font-size: 14pt;
      font-weight: 300;
    }

    .contact-info {
      margin-top: 12px;
    }

    .entry {
      padding-left: 0;
      margin-bottom: 16px;
    }

    .entry-header {
      flex-direction: column;
      align-items: flex-start;
    }

    .entry-date {
      margin-top: 2px;
    }

    .skill-tag {
      background: transparent;
      color: var(--modern-skill-text);
      border: 1px solid var(--modern-skill-border);
    }

    .link {
      color: var(--modern-link);
    }
  ${RESUME_PAGINATION_CSS}
  </style>
</head>
<body>
  <div class="resume">
    <header{{#if profilePicture}} class="header-with-photo"{{/if}}>
      {{#if profilePicture}}
      <img class="profile-photo" src="{{profilePicture.url}}" alt="">
      {{/if}}
      <div class="header-identity">
      <h1><span data-rf="personalInfo.firstName">{{personalInfo.firstName}}</span> <span data-rf="personalInfo.lastName">{{personalInfo.lastName}}</span></h1>
      {{#if profile.headline}}
      <div class="headline" data-rf="profile.headline">{{profile.headline}}</div>
      {{/if}}

      <div class="contact-info">
        {{#if contact.email}}
        <span class="contact-item"><span data-rf="contact.email">{{contact.email}}</span></span>
        {{/if}}
        {{#if contact.phone}}
        <span class="contact-item"><span data-rf="contact.phone">{{contact.phone}}</span></span>
        {{/if}}
        {{#if contact.location}}
        <span class="contact-item"><span data-rf="contact.location">{{contact.location}}</span></span>
        {{/if}}
      </div>

      {{#if links.length}}
      <div class="links">
        {{#each links}}
        <a href="{{url}}" class="link">{{#if label}}{{label}}{{else}}{{type}}{{/if}}</a>
        {{/each}}
      </div>
      {{/if}}
      </div>
    </header>

    {{#if profile.summary}}
    <section class="section">
      <h2>About</h2>
      <div class="summary" data-rf="profile.summary" data-rf-multiline="true">{{{renderContent profile.summary}}}</div>
    </section>
    {{/if}}

    {{#if employment.length}}
    <section class="section">
      <h2>Experience</h2>
      {{#each employment}}
      <div class="entry">
        <div class="entry-header">
          <h3 class="entry-title" data-rf="employment.{{id}}.title">{{title}}</h3>
          <span class="entry-subtitle">{{company}}{{#if location}} · {{location}}{{/if}}</span>
          <span class="entry-date" data-rf-date="employment.{{id}}">
            {{formatDate startDate}} — {{#if isCurrentlyWorking}}Present{{else}}{{formatDate endDate}}{{/if}}
          </span>
        </div>
        <div class="entry-content" data-rf="employment.{{id}}.responsibilities" data-rf-multiline="true">{{{renderContent responsibilities autoList=true}}}</div>
      </div>
      {{/each}}
    </section>
    {{/if}}

    {{#if education.length}}
    <section class="section">
      <h2>Education</h2>
      {{#each education}}
      <div class="entry">
        <div class="entry-header">
          <h3 class="entry-title"><span data-rf="education.{{id}}.degree">{{degree}}</span>{{#if field}} in <span data-rf="education.{{id}}.field">{{field}}</span>{{/if}}</h3>
          <span class="entry-subtitle"><span data-rf="education.{{id}}.institution">{{institution}}</span></span>
          <span class="entry-date" data-rf-date="education.{{id}}">
            {{#if endDate}}{{formatDate endDate}}{{/if}}
          </span>
        </div>
      </div>
      {{/each}}
    </section>
    {{/if}}

    {{#if (or skills.technical.length skills.soft.length)}}
    <section class="section">
      <h2>Skills</h2>
      <div class="skills-grid">
        {{#each skills.technical}}
        <span class="skill-tag" data-rf="skills.technical.{{@index}}">{{this}}</span>
        {{/each}}
        {{#each skills.soft}}
        <span class="skill-tag" data-rf="skills.soft.{{@index}}">{{this}}</span>
        {{/each}}
      </div>
    </section>
    {{/if}}

    {{#if certifications.length}}
    <section class="section">
      <h2>Certifications</h2>
      {{#each certifications}}
      <div class="entry">
        <span class="entry-title"><span data-rf="certifications.{{id}}.name">{{name}}</span></span>
        <span class="entry-subtitle"> · <span data-rf="certifications.{{id}}.issuer">{{issuer}}</span></span>
        {{#if date}}<span class="entry-date" data-rf-date="certifications.{{id}}"> · {{formatDate date}}</span>{{/if}}
      </div>
      {{/each}}
    </section>
    {{/if}}

    {{#each customSections}}
    <section class="section">
      <h2>{{title}}</h2>
      <div class="entry-content" data-rf="customSections.{{id}}.content" data-rf-multiline="true">{{{renderContent content autoList=true}}}</div>
    </section>
    {{/each}}

    {{#if consent.enabled}}{{#if consent.text}}
    <div class="consent-note">{{{consentText consent.text}}}</div>
    {{/if}}{{/if}}
  </div>
</body>
</html>
`;

// Minimal template
const minimalTemplate = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>{{personalInfo.firstName}} {{personalInfo.lastName}} - Resume</title>
  <style>
    ${SHARED_FONT_IMPORTS}
    :root {
      {{colorSchemeCSS}}
      {{templateColorCSS}}
    }
    ${baseStyles}

    body {
      font-family: ${RESUME_FONT_FAMILY};
      font-size: 10.5pt;
    }

    h1 {
      font-size: 20pt;
      font-weight: normal;
      color: var(--resume-color-text);
      text-align: center;
      margin-bottom: 4px;
    }

    h2 {
      font-size: 10pt;
      font-weight: normal;
      text-transform: uppercase;
      letter-spacing: 2px;
      border-bottom: 1px solid var(--resume-color-muted);
      color: var(--resume-color-text);
    }

    .headline {
      text-align: center;
      font-style: italic;
    }

    .contact-info {
      justify-content: center;
      border-bottom: 1px solid var(--resume-color-muted);
      padding-bottom: 12px;
      margin-bottom: 16px;
    }

    .links {
      justify-content: center;
    }

    .skill-tag {
      background: transparent;
      color: var(--resume-color-text);
      padding: 0;
      font-size: 10pt;
    }

    .skill-tag::after {
      content: ' · ';
    }

    .skill-tag:last-child::after {
      content: '';
    }
  ${RESUME_PAGINATION_CSS}
  </style>
</head>
<body>
  <div class="resume">
    <header>
      <h1><span data-rf="personalInfo.firstName">{{personalInfo.firstName}}</span> <span data-rf="personalInfo.lastName">{{personalInfo.lastName}}</span></h1>
      {{#if profile.headline}}
      <div class="headline" data-rf="profile.headline">{{profile.headline}}</div>
      {{/if}}

      <div class="contact-info">
        {{#if contact.email}}<span class="contact-item"><span data-rf="contact.email">{{contact.email}}</span></span>{{/if}}
        {{#if contact.phone}}<span class="contact-item"><span data-rf="contact.phone">{{contact.phone}}</span></span>{{/if}}
        {{#if contact.location}}<span class="contact-item"><span data-rf="contact.location">{{contact.location}}</span></span>{{/if}}
      </div>
    </header>

    {{#if profile.summary}}
    <section class="section">
      <h2>Summary</h2>
      <div class="summary" data-rf="profile.summary" data-rf-multiline="true">{{{renderContent profile.summary}}}</div>
    </section>
    {{/if}}

    {{#if employment.length}}
    <section class="section">
      <h2>Experience</h2>
      {{#each employment}}
      <div class="entry">
        <div class="entry-header">
          <div>
            <span class="entry-title" data-rf="employment.{{id}}.title">{{title}}</span> at <span>{{company}}</span>
          </div>
          <span class="entry-date" data-rf-date="employment.{{id}}">
            {{formatDate startDate}} - {{#if isCurrentlyWorking}}Present{{else}}{{formatDate endDate}}{{/if}}
          </span>
        </div>
        <div class="entry-content" data-rf="employment.{{id}}.responsibilities" data-rf-multiline="true">{{{renderContent responsibilities autoList=true}}}</div>
      </div>
      {{/each}}
    </section>
    {{/if}}

    {{#if education.length}}
    <section class="section">
      <h2>Education</h2>
      {{#each education}}
      <div class="entry">
        <div class="entry-header">
          <span class="entry-title"><span data-rf="education.{{id}}.degree">{{degree}}</span></span>, <span data-rf="education.{{id}}.institution">{{institution}}</span>
          {{#if endDate}}<span class="entry-date" data-rf-date="education.{{id}}">{{formatDate endDate}}</span>{{/if}}
        </div>
      </div>
      {{/each}}
    </section>
    {{/if}}

    {{#if (or skills.technical.length skills.soft.length)}}
    <section class="section">
      <h2>Skills</h2>
      <div class="skills-grid">
        {{#each skills.technical}}<span class="skill-tag" data-rf="skills.technical.{{@index}}">{{this}}</span>{{/each}}
        {{#each skills.soft}}<span class="skill-tag" data-rf="skills.soft.{{@index}}">{{this}}</span>{{/each}}
      </div>
    </section>
    {{/if}}

    {{#each customSections}}
    <section class="section">
      <h2>{{title}}</h2>
      <div class="entry-content" data-rf="customSections.{{id}}.content" data-rf-multiline="true">{{{renderContent content autoList=true}}}</div>
    </section>
    {{/each}}

    {{#if consent.enabled}}{{#if consent.text}}
    <div class="consent-note">{{{consentText consent.text}}}</div>
    {{/if}}{{/if}}
  </div>
</body>
</html>
`;

// Focus-sidebar template - condensed highlights on the left, full narrative on the right
const focusSidebarTemplate = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>{{personalInfo.firstName}} {{personalInfo.lastName}} - Resume</title>
  <style>
    ${SHARED_FONT_IMPORTS}
    :root {
      {{colorSchemeCSS}}
      {{templateColorCSS}}
      --sidebar-bg: var(--resume-color-secondary);
      --sidebar-text: #f8fafc;
      --sidebar-muted: rgba(248, 250, 252, 0.82);
      --sidebar-divider: rgba(248, 250, 252, 0.2);
      --sidebar-pill-bg: rgba(248, 250, 252, 0.1);
      --sidebar-pill-border: rgba(248, 250, 252, 0.26);
      --content-bg: var(--resume-color-background);
      --content-title: var(--resume-color-primary);
      --body-text: var(--resume-color-text);
      --muted-text: var(--resume-color-muted);
      --accent-strong: var(--resume-color-accent);
      --timeline-line: var(--resume-color-muted);
      --entry-dot: var(--resume-color-accent);
      --entry-dot-border: var(--resume-color-primary);
      --avatar-bg: var(--resume-color-accent);
      --avatar-border: rgba(248, 250, 252, 0.3);
      /*
       * Breathing room repeated at the top and bottom of EVERY page.
       *
       * This template prints edge to edge so the sidebar can bleed, which means
       * there is no page margin to inset the text. Pane padding alone only
       * applies once, at the very start and end of the flow, so continuation
       * pages used to begin hard against the paper edge. The panes below clone
       * their decoration onto each fragment to fix that in print; the preview
       * reads this value back to reproduce the same inset per page.
       */
      --resume-page-inset-y: 9.5mm;
    }

    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    /*
     * The two columns are painted on the page canvas, not on .left-pane alone.
     *
     * A root-element background propagates to the canvas and repaints on every
     * page, whereas the flex shell below only spans its own content: its last
     * fragment ends where the text ends, so the sidebar used to stop mid-page
     * and leave bare paper beneath it. min-height: 100vh only ever covered
     * the first page. The panes still paint their own backgrounds on top, so
     * the two agree exactly wherever content exists.
     */
    html {
      width: 100%;
      height: 100%;
      background: linear-gradient(
        to right,
        var(--sidebar-bg) 0,
        var(--sidebar-bg) 33.333%,
        var(--sidebar-divider) 33.333%,
        var(--sidebar-divider) calc(33.333% + 1px),
        var(--content-bg) calc(33.333% + 1px),
        var(--content-bg) 100%
      );
    }

    body {
      font-family: ${RESUME_FONT_FAMILY};
      font-size: 10.5pt;
      line-height: 1.45;
      color: var(--body-text);
      width: 100%;
      min-height: 100%;
      /* Transparent, or it would cover the canvas beneath the last line. */
      background: transparent;
    }

    .resume-shell {
      width: 100%;
      min-height: 100%;
      min-height: 100vh;
      display: flex;
      align-items: stretch;
      background: transparent;
    }

    .left-pane {
      width: 33.333%;
      flex: 0 0 33.333%;
      background: var(--sidebar-bg);
      color: var(--sidebar-text);
      border-right: 1px solid var(--sidebar-divider);
      padding: var(--resume-page-inset-y) 8mm;
      -webkit-box-decoration-break: clone;
      box-decoration-break: clone;
      display: flex;
      flex-direction: column;
      gap: 5.2mm;
    }

    .right-pane {
      width: 66.667%;
      flex: 0 0 66.667%;
      background: var(--content-bg);
      padding: var(--resume-page-inset-y) 10mm;
      -webkit-box-decoration-break: clone;
      box-decoration-break: clone;
    }

    .identity {
      text-align: center;
    }

    .avatar {
      width: 22.5mm;
      height: 22.5mm;
      border-radius: 999px;
      margin: 0 auto 3.2mm;
      background: var(--avatar-bg);
      border: 1px solid var(--avatar-border);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 13.8pt;
      font-weight: 700;
      letter-spacing: 0.4px;
      color: #fff;
    }

    /* Same circle as the initials fallback, filled with the photo instead. */
    .avatar-photo {
      display: block;
      object-fit: cover;
      background: transparent;
    }

    .name {
      font-size: 13.8pt;
      line-height: 1.12;
      margin-bottom: 1.2mm;
      color: var(--sidebar-text);
    }

    .headline {
      color: var(--accent-strong);
      font-size: 8.9pt;
      line-height: 1.3;
      font-weight: 600;
    }


    .sidebar-title {
      color: var(--accent-strong);
      text-transform: uppercase;
      letter-spacing: 2.1px;
      font-size: 9.4pt;
      font-weight: 700;
      margin-bottom: 3mm;
    }

    .contact-list {
      list-style: none;
      display: flex;
      flex-direction: column;
      gap: 2.2mm;
    }

    .contact-item {
      word-break: break-word;
    }

    .contact-label {
      display: block;
      color: var(--sidebar-text);
      font-size: 8.1pt;
      text-transform: uppercase;
      letter-spacing: 1px;
      font-weight: 700;
      margin-bottom: 0.8mm;
    }

    .contact-value {
      color: var(--sidebar-muted);
      font-size: 9.2pt;
      line-height: 1.4;
    }

    .contact-value a {
      color: inherit;
      text-decoration: none;
    }

    .skill-cloud {
      display: flex;
      flex-wrap: wrap;
      gap: 1.8mm;
    }

    .skill-pill {
      display: inline-block;
      border-radius: 3px;
      background: var(--sidebar-pill-bg);
      border: 1px solid var(--sidebar-pill-border);
      color: var(--sidebar-text);
      padding: 0.9mm 2.2mm;
      font-size: 8.8pt;
      line-height: 1.25;
      white-space: normal;
    }

    .main-section {
      margin-bottom: 7.5mm;
    }

    .section-title {
      display: flex;
      align-items: center;
      gap: 2.4mm;
      text-transform: uppercase;
      letter-spacing: 2.5px;
      font-size: 11.2pt;
      font-weight: 700;
      color: var(--content-title);
      margin-bottom: 3.6mm;
    }

    .section-title::before {
      content: '';
      width: 1mm;
      height: 7mm;
      border-radius: 1px;
      background: var(--accent-strong);
      display: inline-block;
    }

    .profile-text {
      font-size: 11pt;
      line-height: 1.58;
      color: var(--body-text);
    }

    .profile-text p + p {
      margin-top: 2mm;
    }

    .timeline {
      position: relative;
      --timeline-padding-left: 5.4mm;
      --timeline-line-left: 1px;
      --timeline-line-width: 1px;
      --timeline-dot-size: 2.6mm;
      --timeline-entry-gap: 6.2mm;
      padding-left: var(--timeline-padding-left);
    }

    .entry {
      position: relative;
      margin-bottom: var(--timeline-entry-gap);
    }

    .entry::before {
      content: '';
      position: absolute;
      width: var(--timeline-dot-size);
      height: var(--timeline-dot-size);
      border-radius: 999px;
      background: var(--entry-dot);
      border: 1px solid var(--entry-dot-border);
      left: calc(var(--timeline-line-left) + (var(--timeline-line-width) / 2) - var(--timeline-padding-left));
      transform: translateX(-50%);
      top: 1.2mm;
      /* Above the connector below, which would otherwise paint over the dot. */
      z-index: 1;
    }

    /*
     * The connector is drawn per entry, not once down the .timeline container.
     *
     * The container spans every entry, so on a page it does not finish it is
     * stretched to the bottom of the fragmentainer and the rule trails away
     * through empty space. An entry never splits (break-inside: avoid), so a
     * segment anchored to one entry ends exactly where that entry ends. Each
     * segment reaches down into the gap below it so consecutive ones join up.
     */
    .entry::after {
      content: '';
      position: absolute;
      left: calc(var(--timeline-line-left) + (var(--timeline-line-width) / 2) - var(--timeline-padding-left));
      transform: translateX(-50%);
      top: 0;
      bottom: calc(-1 * var(--timeline-entry-gap));
      width: var(--timeline-line-width);
      background: var(--timeline-line);
    }

    .entry:last-child::after {
      bottom: 0;
    }

    .entry-header {
      display: flex;
      justify-content: space-between;
      gap: 1.5mm 4mm;
      flex-wrap: wrap;
      align-items: flex-start;
      margin-bottom: 1.6mm;
    }

    .entry-title {
      font-size: 10.8pt;
      font-weight: 700;
      color: var(--content-title);
    }

    .entry-company {
      color: var(--muted-text);
      font-weight: 700;
      margin-left: 1.2mm;
    }

    .entry-date {
      color: var(--muted-text);
      font-size: 9.6pt;
      white-space: nowrap;
      font-weight: 600;
    }

    .entry-content {
      color: var(--body-text);
      font-size: 10.6pt;
      line-height: 1.52;
    }

    .entry-content ul {
      margin-top: 1.8mm;
      margin-left: 5mm;
    }

    .entry-content li {
      margin-bottom: 1.5mm;
    }

    /* Lists recovered from plain-text fields; see lib/resume/list-content.ts. */
    .content-list {
      margin: 1.2mm 0 0 5mm;
      padding: 0;
    }

    .content-list li {
      margin-bottom: 1.5mm;
    }

    /* Consent fine print, at the foot of the main column (see .consent-note in
       the shared base styles). */
    .consent-note {
      margin-top: 5mm;
      font-size: 8pt;
      line-height: 1.35;
      font-style: italic;
      color: var(--muted-text);
    }

    .simple-block {
      margin-bottom: 4mm;
    }

    .simple-label {
      font-weight: 700;
      color: var(--content-title);
    }

    .simple-meta {
      color: var(--muted-text);
      font-size: 9.6pt;
      margin-top: 0.4mm;
    }

    @media screen and (max-width: 680px) {
      html {
        background: var(--content-bg);
      }

      .resume-shell {
        width: 100%;
        min-height: auto;
        display: block;
      }

      .left-pane,
      .right-pane {
        width: 100%;
        flex: none;
        padding: 22px 20px;
      }

      .timeline {
        --timeline-padding-left: 16px;
        --timeline-line-left: 4px;
        padding-left: var(--timeline-padding-left);
      }
    }

    @media print {
      body {
        margin: 0;
        background: #fff;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .resume-shell {
        width: 100% !important;
        min-height: 100vh !important;
        display: flex !important;
      }
    }
  ${RESUME_PAGINATION_CSS}
  </style>
</head>
<body>
  <div class="resume-shell">
    <aside class="left-pane">
      <section class="identity">
        {{#if profilePicture}}
        <img class="avatar avatar-photo" src="{{profilePicture.url}}" alt="">
        {{else}}
        <div class="avatar">{{initials personalInfo.firstName personalInfo.lastName}}</div>
        {{/if}}
        <div class="name"><span data-rf="personalInfo.firstName">{{personalInfo.firstName}}</span> <span data-rf="personalInfo.lastName">{{personalInfo.lastName}}</span></div>
        {{#if profile.headline}}
        <div class="headline" data-rf="profile.headline">{{profile.headline}}</div>
        {{/if}}
      </section>

      {{#if (or skills.technical.length skills.soft.length skills.categories.length)}}
      <section class="sidebar-section">
        <div class="sidebar-title">Core Skills</div>
        <div class="skill-cloud">
          {{#each skills.technical}}<span class="skill-pill" data-rf="skills.technical.{{@index}}">{{this}}</span>{{/each}}
          {{#each skills.soft}}<span class="skill-pill" data-rf="skills.soft.{{@index}}">{{this}}</span>{{/each}}
          {{#each skills.categories}}
          {{#each skills}}<span class="skill-pill" data-rf="skillCategories.{{../id}}.{{@index}}">{{this}}</span>{{/each}}
          {{/each}}
        </div>
      </section>
      {{/if}}

      {{#if (or contact.location contact.email contact.phone)}}
      <section class="sidebar-section">
        <div class="sidebar-title">Contact</div>
        <ul class="contact-list">
          {{#if contact.location}}
          <li class="contact-item">
            <span class="contact-label">Location</span>
            <span class="contact-value"><span data-rf="contact.location">{{contact.location}}</span></span>
          </li>
          {{/if}}
          {{#if contact.email}}
          <li class="contact-item">
            <span class="contact-label">Email</span>
            <span class="contact-value"><span data-rf="contact.email">{{contact.email}}</span></span>
          </li>
          {{/if}}
          {{#if contact.phone}}
          <li class="contact-item">
            <span class="contact-label">Phone</span>
            <span class="contact-value"><span data-rf="contact.phone">{{contact.phone}}</span></span>
          </li>
          {{/if}}
        </ul>
      </section>
      {{/if}}

      {{#if links.length}}
      <section class="sidebar-section">
        <div class="sidebar-title">Profiles</div>
        <ul class="contact-list">
          {{#each links}}
          <li class="contact-item">
            <span class="contact-label">{{#if label}}{{label}}{{else}}{{type}}{{/if}}</span>
            <span class="contact-value">{{url}}</span>
          </li>
          {{/each}}
        </ul>
      </section>
      {{/if}}
    </aside>

    <main class="right-pane">
      {{#if profile.summary}}
      <section class="main-section">
        <div class="section-title">Profile</div>
        <div class="profile-text" data-rf="profile.summary" data-rf-multiline="true">{{{renderContent profile.summary}}}</div>
      </section>
      {{/if}}

      {{#if employment.length}}
      <section class="main-section">
        <div class="section-title">Experience</div>
        <div class="timeline">
          {{#each employment}}
          <article class="entry">
            <div class="entry-header">
              <div class="entry-title"><span data-rf="employment.{{id}}.title">{{title}}</span> <span class="entry-company" data-rf="employment.{{id}}.company">{{company}}</span>{{#if location}}<span class="entry-company"> - {{location}}</span>{{/if}}</div>
              <div class="entry-date" data-rf-date="employment.{{id}}">{{formatDate startDate}} - {{#if isCurrentlyWorking}}Present{{else}}{{formatDate endDate}}{{/if}}</div>
            </div>
            <div class="entry-content" data-rf="employment.{{id}}.responsibilities" data-rf-multiline="true">{{{renderContent responsibilities autoList=true}}}</div>
          </article>
          {{/each}}
        </div>
      </section>
      {{/if}}

      {{#if education.length}}
      <section class="main-section">
        <div class="section-title">Education</div>
        {{#each education}}
        <div class="simple-block">
          <div class="simple-label"><span data-rf="education.{{id}}.degree">{{degree}}</span>{{#if field}} in <span data-rf="education.{{id}}.field">{{field}}</span>{{/if}}</div>
          <div class="simple-meta"><span data-rf="education.{{id}}.institution">{{institution}}</span>{{#if endDate}} · {{formatDate endDate}}{{else if startDate}} · {{formatDate startDate}}{{/if}}</div>
          {{#if gpa}}<div class="simple-meta">GPA: <span data-rf="education.{{id}}.gpa">{{gpa}}</span></div>{{/if}}
        </div>
        {{/each}}
      </section>
      {{/if}}

      {{#if certifications.length}}
      <section class="main-section">
        <div class="section-title">Certifications</div>
        {{#each certifications}}
        <div class="simple-block">
          <div class="simple-label"><span data-rf="certifications.{{id}}.name">{{name}}</span></div>
          <div class="simple-meta"><span data-rf="certifications.{{id}}.issuer">{{issuer}}</span>{{#if date}} · {{formatDate date}}{{/if}}</div>
        </div>
        {{/each}}
      </section>
      {{/if}}

      {{#each customSections}}
      <section class="main-section">
        <div class="section-title" data-rf="customSections.{{id}}.title">{{title}}</div>
        <div class="entry-content" data-rf="customSections.{{id}}.content" data-rf-multiline="true">{{{renderContent content autoList=true}}}</div>
      </section>
      {{/each}}

      {{#if consent.enabled}}{{#if consent.text}}
      <div class="consent-note">{{{consentText consent.text}}}</div>
      {{/if}}{{/if}}
    </main>
  </div>
</body>
</html>
`;

// ATS-optimized template - parsing-safe, single-column, standard headings
const atsOptimizedTemplate = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>{{personalInfo.firstName}} {{personalInfo.lastName}} - Resume</title>
  <style>
    :root {
      {{colorSchemeCSS}}
      {{templateColorCSS}}
    }

    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    body {
      font-family: Arial, Calibri, 'Helvetica Neue', sans-serif;
      font-size: 11pt;
      line-height: 1.35;
      color: #111827;
      background: #ffffff;
    }

    .resume {
      width: 210mm;
      min-height: 297mm;
      padding: 14mm;
      margin: 0 auto;
      background: #ffffff;
    }

    h1 {
      font-size: 22pt;
      line-height: 1.15;
      font-weight: 700;
      color: #111827;
      margin-bottom: 4px;
    }

    h2 {
      font-size: 11pt;
      color: #111827;
      margin: 14px 0 8px;
      padding-bottom: 3px;
      border-bottom: 1px solid #4b5563;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    h3 {
      font-size: 10.5pt;
      font-weight: 600;
      color: #111827;
    }

    .headline {
      font-size: 11pt;
      color: #374151;
      margin-bottom: 6px;
    }

    .contact-line,
    .links-line {
      font-size: 10pt;
      color: #374151;
      margin-bottom: 4px;
    }

    .contact-line span:not(:last-child)::after,
    .links-line span:not(:last-child)::after {
      content: ' | ';
    }

    .section {
      margin-bottom: 11px;
    }

    .summary,
    .entry-content {
      font-size: 10pt;
    }

    .entry {
      margin-bottom: 10px;
    }

    .entry-header {
      margin-bottom: 4px;
    }

    .entry-subtitle,
    .entry-date {
      font-size: 10pt;
      color: #374151;
    }

    .entry-content ul {
      margin: 4px 0 0 18px;
      padding: 0;
    }

    .entry-content li {
      margin-bottom: 2px;
    }

    /* Lists recovered from plain-text fields; see lib/resume/list-content.ts. */
    .content-list {
      margin: 4px 0 0 18px;
      padding: 0;
    }

    .content-list li {
      margin-bottom: 2px;
    }

    /* Consent fine print. This template hardcodes its palette to stay
       parsing-safe, so the muted grey is spelled out rather than themed. */
    .consent-note {
      margin-top: 14px;
      font-size: 8pt;
      line-height: 1.35;
      color: #4b5563;
    }

    .skills-group {
      font-size: 10pt;
      margin-bottom: 4px;
    }

    .skills-label {
      font-weight: 600;
    }

    .skills-item:not(:last-child)::after {
      content: ', ';
    }

    @media print {
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .resume {
        width: 100% !important;
        min-height: auto !important;
        padding: 0 !important;
        margin: 0 !important;
      }
    }
  ${RESUME_PAGINATION_CSS}
  </style>
</head>
<body>
  <div class="resume">
    <header>
      <h1><span data-rf="personalInfo.firstName">{{personalInfo.firstName}}</span> <span data-rf="personalInfo.lastName">{{personalInfo.lastName}}</span></h1>
      {{#if profile.headline}}
      <div class="headline" data-rf="profile.headline">{{profile.headline}}</div>
      {{/if}}

      <div class="contact-line">
        {{#if contact.email}}<span><span data-rf="contact.email">{{contact.email}}</span></span>{{/if}}
        {{#if contact.phone}}<span><span data-rf="contact.phone">{{contact.phone}}</span></span>{{/if}}
        {{#if contact.location}}<span><span data-rf="contact.location">{{contact.location}}</span></span>{{/if}}
      </div>

      {{#if links.length}}
      <div class="links-line">
        {{#each links}}
        <span>{{#if label}}{{label}}: {{/if}}{{url}}</span>
        {{/each}}
      </div>
      {{/if}}
    </header>

    {{#if profile.summary}}
    <section class="section">
      <h2>Professional Summary</h2>
      <div class="summary" data-rf="profile.summary" data-rf-multiline="true">{{{renderContent profile.summary}}}</div>
    </section>
    {{/if}}

    {{#if employment.length}}
    <section class="section">
      <h2>Work Experience</h2>
      {{#each employment}}
      <div class="entry">
        <div class="entry-header">
          <h3>{{title}}</h3>
          <div class="entry-subtitle">{{company}}{{#if location}} | {{location}}{{/if}}</div>
          <div class="entry-date" data-rf-date="employment.{{id}}">
            {{formatDate startDate}} - {{#if isCurrentlyWorking}}Present{{else}}{{formatDate endDate}}{{/if}}
          </div>
        </div>
        <div class="entry-content" data-rf="employment.{{id}}.responsibilities" data-rf-multiline="true">{{{renderContent responsibilities autoList=true}}}</div>
      </div>
      {{/each}}
    </section>
    {{/if}}

    {{#if education.length}}
    <section class="section">
      <h2>Education</h2>
      {{#each education}}
      <div class="entry">
        <div class="entry-header">
          <h3><span data-rf="education.{{id}}.degree">{{degree}}</span>{{#if field}} in <span data-rf="education.{{id}}.field">{{field}}</span>{{/if}}</h3>
          <div class="entry-subtitle"><span data-rf="education.{{id}}.institution">{{institution}}</span></div>
          <div class="entry-date" data-rf-date="education.{{id}}">
            {{#if startDate}}{{formatDate startDate}}{{/if}}
            {{#if endDate}} - {{formatDate endDate}}{{/if}}
          </div>
        </div>
        {{#if gpa}}
        <div class="entry-content">GPA: <span data-rf="education.{{id}}.gpa">{{gpa}}</span></div>
        {{/if}}
      </div>
      {{/each}}
    </section>
    {{/if}}

    {{#if (or skills.technical.length skills.soft.length skills.categories.length)}}
    <section class="section">
      <h2>Skills</h2>

      {{#if skills.technical.length}}
      <div class="skills-group">
        <span class="skills-label">Technical Skills: </span>
        {{#each skills.technical}}<span class="skills-item" data-rf="skills.technical.{{@index}}">{{this}}</span>{{/each}}
      </div>
      {{/if}}

      {{#if skills.soft.length}}
      <div class="skills-group">
        <span class="skills-label">Soft Skills: </span>
        {{#each skills.soft}}<span class="skills-item" data-rf="skills.soft.{{@index}}">{{this}}</span>{{/each}}
      </div>
      {{/if}}

      {{#each skills.categories}}
      <div class="skills-group">
        <span class="skills-label">{{name}}: </span>
        {{#each skills}}<span class="skills-item" data-rf="skillCategories.{{../id}}.{{@index}}">{{this}}</span>{{/each}}
      </div>
      {{/each}}
    </section>
    {{/if}}

    {{#if certifications.length}}
    <section class="section">
      <h2>Certifications</h2>
      {{#each certifications}}
      <div class="entry">
        <div class="entry-header">
          <h3><span data-rf="certifications.{{id}}.name">{{name}}</span></h3>
          <div class="entry-subtitle"><span data-rf="certifications.{{id}}.issuer">{{issuer}}</span></div>
          {{#if date}}<div class="entry-date" data-rf-date="certifications.{{id}}">{{formatDate date}}</div>{{/if}}
        </div>
      </div>
      {{/each}}
    </section>
    {{/if}}

    {{#each customSections}}
    <section class="section">
      <h2>{{title}}</h2>
      <div class="entry-content" data-rf="customSections.{{id}}.content" data-rf-multiline="true">{{{renderContent content autoList=true}}}</div>
    </section>
    {{/each}}

    {{#if consent.enabled}}{{#if consent.text}}
    <div class="consent-note">{{{consentText consent.text}}}</div>
    {{/if}}{{/if}}
  </div>
</body>
</html>
`;

// Template map
const templates: Record<string, string> = {
  classic: classicTemplate,
  modern: modernTemplate,
  minimal: minimalTemplate,
  'focus-sidebar': focusSidebarTemplate,
  'ats-optimized': atsOptimizedTemplate,
  // Additional templates will use classic as fallback
  professional: classicTemplate,
  creative: modernTemplate,
  executive: classicTemplate,
  technical: modernTemplate,
  academic: minimalTemplate,
  compact: minimalTemplate,
  elegant: minimalTemplate,
};

// Register the 'or' helper
Handlebars.registerHelper('or', function (...args) {
  // Remove the options object from the end
  args.pop();
  return args.some(Boolean);
});

/**
 * `{{#if profilePicture}}` for the templates, or `null` to suppress the photo.
 *
 * Two gates, both deliberate. The template must declare that it lays a photo
 * out at all — several ids share one Handlebars source, so the markup alone
 * cannot decide — and the stored value must be a URL that is safe to put in an
 * `src`. The value is whatever the user has ever had in `profilePictureUrl`,
 * including arbitrary external URLs typed into the field this feature replaced,
 * so it is screened rather than trusted.
 */
function buildProfilePictureData(url: unknown, templateId?: string): { url: string } | null {
  if (!templateSupportsProfilePhoto(templateId)) return null;
  if (!isRenderableProfilePictureUrl(url)) return null;
  return { url: url.trim() };
}

/**
 * Render resume content to HTML using the specified template and color scheme
 */
export function renderResumeHtml(content: ResumeContent, settings: ResumeSettings): string {
  const templateSource = templates[settings.templateId] || templates.classic;
  const colorScheme = getColorScheme(settings.colorSchemeId) || getDefaultColorScheme();
  const normalizedContent = normalizeResumeContent(content);

  const template = Handlebars.compile(templateSource);

  const data = {
    ...normalizedContent,
    profilePicture: buildProfilePictureData(
      normalizedContent.personalInfo?.profilePictureUrl,
      settings.templateId
    ),
    colorSchemeCSS: generateColorSchemeCSS(colorScheme),
    templateColorCSS: generateTemplateOverrideCSS(colorScheme, settings.templateId),
  };

  return template(data);
}

/**
 * Get the HTML template source for preview iframe
 */
export function getTemplateSource(templateId: string): string {
  return templates[templateId] || templates.classic;
}
